import { memo, useMemo } from 'react';
import { isEmpty } from 'lodash';
import {
  Typography, Stack, Link as MuiLink, Card,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PersonIcon from '@mui/icons-material/Person';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { faSpotify } from '@fortawesome/free-brands-svg-icons';
import { RoundedMedia } from '@client/components/common/Media';
import { getExternalUrl } from '@sda/utils/url';
import { MediaEmbed } from '@client/components/common/MediaEmbed';
import { ProfileTypes } from '@sda/enums';
import { Description } from '@client/components/common/Description';
import { getImageLargeUrl } from '@sda/utils/image';
import { getEventLocation, getRatingString, getRoundedFollowers } from '@sda/utils/string';
import { FeaturedReview } from '@client/components/common/Review/FeaturedReview';
import { getProfileRespondsQuickly } from '@sda/utils/meta';
import { FontAwesomeIcon } from '@client/components/common/FontAwesomeIcon';
import { ShowCalls } from './ShowCalls';
import { Shows } from './Shows';
import { GenreLabels } from './GenreLabels';
import { VenueTypeLabel } from './VenueTypeLabel';
import { HostMap } from './Venue/HostMap';
import type { FetchDataType } from '@sda/sdk/interfaces';
import type { OptionalUnion } from '@sda/helpers/helpers.types';
import type { PublicAct } from '@sda/interfaces/act';
import type { PublicVenue } from '@sda/interfaces/venue';

const useStyles = makeStyles()((theme) => ({
  requirements: {
    '& > svg': {
      marginRight: theme.spacing(1),
      fontSize: 20,
    },

    '& > svg, & > span': {
      verticalAlign: 'middle',
    },
  },
  emptyImageCard: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.radii.large,
    marginBottom: theme.spacing(2),
    position: 'relative',
    aspectRatio: '16 / 9',

    '&::before': {
      content: '""',
      display: 'block',
      paddingTop: '56.25%',
    },
  },
  personIcon: {
    fontSize: 150,
    color: theme.palette.grey[400],
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',

    [theme.breakpoints.up('md')]: {
      fontSize: 250,
    },
  },
}));

export const Profile = memo<OptionalUnion<PublicAct, PublicVenue> & FetchDataType>(({ ...profile }) => {
  const { classes } = useStyles();
  const profileRespondsQuickly = useMemo(() => getProfileRespondsQuickly(profile), [profile]);

  if (!profile?.id) {
    return null;
  }

  const hasGenre = Boolean(profile.primaryGenre) || Boolean(profile.secondaryGenre);
  const hasVenueType = Boolean(profile.venueType);

  return (
    <Stack spacing={4}>
      <div>
        {isEmpty(profile.publicImageUrl) ? (
          <Card className={classes.emptyImageCard}>
            <PersonIcon className={classes.personIcon} />
          </Card>
        ) : (
          <RoundedMedia
            title={profile.name}
            images={profile.publicImageUrl}
            sizeCallback={getImageLargeUrl}
            loading="eager"
            badgeId={profile.badge}
            enableCarousel
          />
        )}

        <Typography
          variant="h6"
          paragraph={!hasGenre && !hasVenueType}
        >
          {profile.name}
        </Typography>

        {Boolean(hasGenre) && (
          <GenreLabels
            primaryGenre={profile.primaryGenre}
            secondaryGenre={profile.secondaryGenre}
            paragraph
          />
        )}

        {Boolean(hasVenueType) && (
          <VenueTypeLabel
            venueType={profile.venueType}
            paragraph
          />
        )}

        <Stack spacing={2}>
          <Typography
            variant="body1"
            color="textSecondary"
          >
            {getEventLocation(profile)}
          </Typography>

          <div>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classes.requirements}
              paragraph
            >
              <FavoriteIcon color="error" />

              <span>
                {getRatingString(profile.score, profile.numReviews)}
              </span>
            </Typography>

            {profile.exactCapacity > 0 && (
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.requirements}
                paragraph
              >
                <AccessibilityNewIcon color="success" />

                <span>
                  Capacity:
                  {' '}

                  {profile.exactCapacity}
                </span>
              </Typography>
            )}

            {profile.spotifyMonthlyActiveListeners > 0 && (
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.requirements}
                paragraph
              >
                <FontAwesomeIcon
                  icon={faSpotify}
                  color="success"
                />

                <span>
                  <MuiLink
                    id="sda-profile-spotify-link"
                    href={profile.spotifyArtistUrl}
                    target="_blank"
                    rel="noreferrer"
                    color="textSecondary"
                    title={`${profile.name} Spotify profile`}
                  >
                    {getRoundedFollowers(profile.spotifyMonthlyActiveListeners)}

                    {' '}
                    monthly listeners
                  </MuiLink>
                </span>
              </Typography>
            )}

            {profile.hasPA && (
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.requirements}
                paragraph
              >
                <VolumeUpIcon color="warning" />

                <span>
                  {profile.profileType === ProfileTypes.ACT ? 'Requires a PA system' : 'Has a PA system'}
                </span>
              </Typography>
            )}

            {Boolean(profileRespondsQuickly) && (
              <Typography
                variant="body1"
                color="textSecondary"
                className={classes.requirements}
              >
                <ScheduleIcon color="info" />

                <span>
                  Responds quickly
                </span>
              </Typography>
            )}
          </div>

          <Description variant="subtitle1">
            {profile.description}
          </Description>

          {Boolean(profile.websiteUrl) && (
            <MuiLink
              id="sda-call-website-link"
              href={getExternalUrl(profile.websiteUrl)}
              target="_blank"
              rel="noopener noreferrer"
              variant="subtitle1"
            >
              Website
            </MuiLink>
          )}

          {profile.profileType === ProfileTypes.VENUE && Boolean(profile.fuzzyLatLng) && (
            <HostMap
              location={profile.fuzzyLatLng}
            />
          )}

          {Boolean(profile.featuredReviewId) && (
            <FeaturedReview reviewId={profile.featuredReviewId} />
          )}

          <MediaEmbed
            mediaType="video"
            {...profile}
          />

          <MediaEmbed
            mediaType="audio"
            {...profile}
          />
        </Stack>
      </div>

      <Shows profileId={profile.id} />

      <ShowCalls profileId={profile.id} />

      <Shows
        profileId={profile.id}
        displayPast
      />
    </Stack>
  );
});
